import { Star, Store, User, MessageSquare, Check, ShieldCheck } from "lucide-react";

/**
 * TwoSidedFlow — hero animation for the Servrevu landing page.
 *
 * Visualises the platform's two-sided reputation loop: a customer and a
 * business review each other, with stars & speech bubbles flowing in both
 * directions along an SVG path. Pure CSS keyframes — no runtime cost, no
 * external animation library.
 */
export default function TwoSidedFlow() {
  return (
    <section
      className="relative overflow-hidden bg-white border-b border-[#EAE3DE]"
      data-testid="two-sided-flow-section"
    >
      {/* Warm background wash */}
      <div
        aria-hidden
        className="absolute inset-0 bg-gradient-to-b from-[#FDFBF9] via-white to-[#FAF5F2]"
      />

      <div className="relative max-w-6xl mx-auto px-6 md:px-10 py-20 md:py-24">
        <div className="max-w-2xl mx-auto text-center mb-14">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[#F9E4E1] text-[#B31E1E] border border-[#F0C9C4] px-3 py-1 text-[11px] font-semibold tracking-wide">
            <MessageSquare size={11} /> The Servrevu loop
          </span>
          <h2 className="mt-4 text-3xl md:text-5xl font-black tracking-tight text-[#1B1614] leading-[1.05]">
            Reviews travel <span className="text-[#B31E1E]">both ways</span>.
          </h2>
          <p className="mt-4 text-[17px] text-[#5C4E48]">
            Every transaction leaves a trail on both sides. Watch a rating move.
          </p>
        </div>

        {/* Animation stage */}
        <div className="relative mx-auto max-w-4xl h-[380px] md:h-[420px]" data-testid="ts-flow-stage">
          {/* ─────── CUSTOMER CARD (left) ─────── */}
          <div className="ts-card ts-card-left" data-testid="ts-customer-card">
            <div className="ts-card-inner">
              <div className="ts-badge">
                <User size={13} className="text-[#B31E1E]" />
                <span>Customer</span>
              </div>
              <div className="mt-4 flex items-center gap-3">
                <div className="ts-avatar ts-avatar-customer">SM</div>
                <div>
                  <div className="font-semibold text-[#1B1614] leading-tight">Sarah M.</div>
                  <div className="text-xs text-[#8B7B73] font-mono">Berkeley, CA</div>
                </div>
              </div>
              <div className="ts-stars mt-3">
                {[0, 1, 2, 3, 4].map((i) => (
                  <Star key={i} size={14} className="fill-[#B31E1E] text-[#B31E1E]" />
                ))}
                <span className="ml-2 text-xs text-[#5C4E48] font-mono">12 reviews</span>
              </div>
              <div className="ts-quote">
                <span className="ts-quote-mark">&ldquo;</span>
                Always on time. Great tipper.
                <span className="ts-quote-mark ts-quote-mark-end">&rdquo;</span>
              </div>
            </div>
          </div>

          {/* ─────── BUSINESS CARD (right) ─────── */}
          <div className="ts-card ts-card-right" data-testid="ts-business-card">
            <div className="ts-card-inner">
              <div className="ts-badge">
                <Store size={13} className="text-[#B31E1E]" />
                <span>Business</span>
              </div>
              <div className="mt-4 flex items-center gap-3">
                <div className="ts-avatar ts-avatar-business">BB</div>
                <div>
                  <div className="font-semibold text-[#1B1614] leading-tight">Blue Bottle Coffee</div>
                  <div className="text-xs text-[#8B7B73] font-mono">Coffee · Oakland</div>
                </div>
              </div>
              <div className="ts-stars mt-3">
                {[0, 1, 2, 3, 4].map((i) => (
                  <Star key={i} size={14} className="fill-[#B31E1E] text-[#B31E1E]" />
                ))}
                <span className="ml-2 text-xs text-[#5C4E48] font-mono">247 reviews</span>
              </div>
              <div className="ts-quote">
                <span className="ts-quote-mark">&ldquo;</span>
                Best pour-over in the East Bay.
                <span className="ts-quote-mark ts-quote-mark-end">&rdquo;</span>
              </div>
            </div>
          </div>

          {/* ─────── CONNECTION PATH + TRAVELING STARS ─────── */}
          <svg
            className="ts-flow-svg"
            viewBox="0 0 600 200"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden
          >
            <defs>
              <linearGradient id="ts-line-grad" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#F0C9C4" />
                <stop offset="50%" stopColor="#B31E1E" />
                <stop offset="100%" stopColor="#F0C9C4" />
              </linearGradient>
              {/* Curved upper path (customer → business) */}
              <path
                id="ts-path-top"
                d="M 20 100 C 200 40, 400 40, 580 100"
                fill="none"
              />
              {/* Curved lower path (business → customer) */}
              <path
                id="ts-path-bottom"
                d="M 580 100 C 400 160, 200 160, 20 100"
                fill="none"
              />
            </defs>

            {/* Static tracks */}
            <use href="#ts-path-top" stroke="url(#ts-line-grad)" strokeWidth="1.5" strokeDasharray="4 6" opacity="0.55" />
            <use href="#ts-path-bottom" stroke="url(#ts-line-grad)" strokeWidth="1.5" strokeDasharray="4 6" opacity="0.55" />

            {/* Traveling stars — customer → business (top arc, red) */}
            {[0, 1, 2].map((i) => (
              <g key={`top-${i}`} className="ts-star" style={{ animationDelay: `${i * 1.2}s` }}>
                <animateMotion dur="3.6s" repeatCount="indefinite" begin={`${i * 1.2}s`}>
                  <mpath href="#ts-path-top" />
                </animateMotion>
                <circle r="12" fill="#B31E1E" opacity="0.15" />
                <circle r="7" fill="#B31E1E" />
                <text
                  x="0" y="0" textAnchor="middle" dominantBaseline="central"
                  fontSize="10" fontWeight="900" fill="white"
                >★</text>
              </g>
            ))}

            {/* Traveling stars — business → customer (bottom arc, dark) */}
            {[0, 1, 2].map((i) => (
              <g key={`bot-${i}`} className="ts-star" style={{ animationDelay: `${i * 1.2 + 0.6}s` }}>
                <animateMotion dur="3.6s" repeatCount="indefinite" begin={`${i * 1.2 + 0.6}s`}>
                  <mpath href="#ts-path-bottom" />
                </animateMotion>
                <circle r="12" fill="#1B1614" opacity="0.12" />
                <circle r="7" fill="#1B1614" />
                <text
                  x="0" y="0" textAnchor="middle" dominantBaseline="central"
                  fontSize="10" fontWeight="900" fill="white"
                >★</text>
              </g>
            ))}
          </svg>

          {/* Center receipt / handshake badge */}
          <div className="ts-center" aria-hidden>
            <div className="ts-center-ring">
              <div className="ts-center-inner">
                <Check size={16} className="text-white" />
              </div>
            </div>
            <div className="ts-center-label">Mutual trust</div>
          </div>
        </div>

        {/* Legend row */}
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-3 max-w-2xl mx-auto text-sm">
          <div className="flex items-center gap-2 rounded-lg border border-[#EAE3DE] bg-white/70 px-4 py-3">
            <span className="w-3 h-3 rounded-full bg-[#B31E1E] shrink-0" />
            <span className="text-[#3E2A26]"><strong>Customer rates business</strong> — the review you already know</span>
          </div>
          <div className="flex items-center gap-2 rounded-lg border border-[#EAE3DE] bg-white/70 px-4 py-3">
            <span className="w-3 h-3 rounded-full bg-[#1B1614] shrink-0" />
            <span className="text-[#3E2A26]"><strong>Business rates customer</strong> — the review nobody has, until now</span>
          </div>
        </div>

        {/* Fair-mediation callout — both sides can dispute with real evidence */}
        <div
          className="mt-6 max-w-3xl mx-auto rounded-xl border border-[#F0C9C4] bg-gradient-to-r from-[#FDF3F1] via-white to-[#FDF3F1] px-5 py-4 flex items-start gap-3"
          data-testid="ts-flow-mediation-note"
        >
          <div className="w-9 h-9 rounded-lg bg-[#B31E1E] flex items-center justify-center shrink-0">
            <ShieldCheck size={16} className="text-white" />
          </div>
          <p className="text-[14px] text-[#3E2A26] leading-relaxed">
            <strong className="text-[#1B1614]">Fair mediation, both sides.</strong>{" "}
            Customers <em>and</em> businesses can dispute any negative review with real documentation — photos, video clips, audio recordings, text-message screenshots — and request mediation. Our team reviews the evidence from both parties before a verdict lands, so no rating stands unchallenged.
          </p>
        </div>
      </div>

      <style>{`
        /* Card positions */
        .ts-card {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          width: min(45%, 260px);
          z-index: 2;
          animation: ts-float 6s ease-in-out infinite;
        }
        .ts-card-left  { left: 0;  }
        .ts-card-right { right: 0; animation-delay: -3s; }
        .ts-card-inner {
          background: #ffffff;
          border: 1px solid #EAE3DE;
          border-radius: 16px;
          padding: 18px 18px 20px;
          box-shadow: 0 12px 40px -18px rgba(179,30,30,0.28), 0 2px 8px -4px rgba(0,0,0,0.06);
          position: relative;
        }
        .ts-card-inner::before {
          content: "";
          position: absolute;
          inset: -1px;
          border-radius: 16px;
          padding: 1px;
          background: linear-gradient(140deg, rgba(179,30,30,0.35), transparent 40%, transparent 60%, rgba(179,30,30,0.35));
          -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
          -webkit-mask-composite: xor;
                  mask-composite: exclude;
          pointer-events: none;
          opacity: 0.75;
        }

        .ts-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 4px 10px;
          border-radius: 999px;
          background: #FAF5F2;
          border: 1px solid #EAE3DE;
          font-size: 11px;
          font-weight: 600;
          color: #3E2A26;
          letter-spacing: 0.02em;
        }

        .ts-avatar {
          width: 44px;
          height: 44px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 800;
          font-size: 14px;
          letter-spacing: 0.05em;
          color: white;
          flex-shrink: 0;
        }
        .ts-avatar-customer {
          background: linear-gradient(135deg, #B31E1E 0%, #8F1717 100%);
        }
        .ts-avatar-business {
          background: linear-gradient(135deg, #1B1614 0%, #3E2A26 100%);
        }

        .ts-stars {
          display: flex;
          align-items: center;
          gap: 2px;
        }

        .ts-quote {
          margin-top: 12px;
          padding: 8px 12px;
          border-radius: 10px;
          background: #FAF5F2;
          font-size: 12.5px;
          color: #3E2A26;
          line-height: 1.45;
          position: relative;
          border: 1px dashed #EADAD3;
        }
        .ts-quote-mark {
          color: #B31E1E;
          font-weight: 700;
          margin-right: 2px;
          font-family: Georgia, serif;
        }
        .ts-quote-mark-end { margin-left: 2px; margin-right: 0; }

        /* Center badge */
        .ts-center {
          position: absolute;
          left: 50%;
          top: 50%;
          transform: translate(-50%, -50%);
          z-index: 3;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
        }
        .ts-center-ring {
          width: 56px;
          height: 56px;
          border-radius: 50%;
          background: white;
          border: 2px solid #F0C9C4;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 4px 20px -6px rgba(179,30,30,0.35);
          animation: ts-pulse 2.4s ease-in-out infinite;
        }
        .ts-center-inner {
          width: 34px;
          height: 34px;
          border-radius: 50%;
          background: #B31E1E;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .ts-center-label {
          font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
          font-size: 10px;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          color: #8B7B73;
          background: white;
          padding: 2px 8px;
          border-radius: 999px;
          border: 1px solid #EAE3DE;
        }

        /* SVG occupies the whole stage behind the cards */
        .ts-flow-svg {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          z-index: 1;
        }

        .ts-star text {
          font-family: ui-sans-serif, system-ui, sans-serif;
        }

        /* Float animation gives the cards subtle life without being annoying */
        @keyframes ts-float {
          0%, 100% { transform: translateY(calc(-50% - 4px)); }
          50%      { transform: translateY(calc(-50% + 4px)); }
        }

        @keyframes ts-pulse {
          0%, 100% { transform: scale(1); box-shadow: 0 4px 20px -6px rgba(179,30,30,0.35); }
          50%      { transform: scale(1.08); box-shadow: 0 6px 28px -6px rgba(179,30,30,0.55); }
        }

        @media (prefers-reduced-motion: reduce) {
          .ts-card, .ts-center-ring { animation: none !important; }
        }
      `}</style>
    </section>
  );
}
